import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, AlertCircle, MailPlus, UserPlus, Link as LinkIcon, QrCode, Users, User } from 'lucide-react';
import { toast } from 'sonner';
import Button from './Button';
import Input from './Input';
import Card from './Card';
import QRCodeDisplay from './QRCodeDisplay';
import { OroLoader } from './OroLoader';
import { invitesApi, inviteDeliveryFailure, inviteDeliveryFailureMessage, type InviteDeliveryFailure } from '../lib/api/invites';
import { resolveInviteRecipient } from '../lib/inviteRecipient';

interface ClubMemberInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  clubName: string;
  /** Whether current user is a leader (can invite as leader) - #912 */
  isLeader?: boolean;
}

const ClubMemberInviteModal: React.FC<ClubMemberInviteModalProps> = ({
  isOpen,
  onClose,
  clubId,
  clubName,
  isLeader = false,
}) => {
  // #1016: Single vs Multiple invite mode
  const [inviteMode, setInviteMode] = useState<'single' | 'multiple'>('multiple');
  // #2623: single recipient field accepts an email OR a phone number.
  const [recipient, setRecipient] = useState('');
  const [inviteType, setInviteType] = useState<'club_member' | 'club_leader'>('club_member');
  const [expiresInDays, setExpiresInDays] = useState(365); // Default to 365 for multiple mode
  const [inviteSent, setInviteSent] = useState(false);
  // #3384: the invite was created but its email/text did not go out.
  const [deliveryFailure, setDeliveryFailure] = useState<InviteDeliveryFailure | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInviteMode('multiple');
      setRecipient('');
      setInviteType('club_member');
      setExpiresInDays(365);
      setError('');
      setInviteUrl('');
      setCopied(false);
      setGeneratingLink(false);
      setInviteSent(false);
      setShowQR(false);
    }
  }, [isOpen]);

  // #1016: Update defaults when mode changes
  useEffect(() => {
    if (inviteMode === 'multiple') {
      setExpiresInDays(365);
      setInviteType('club_member'); // Multi-use can only be member
    } else {
      setExpiresInDays(30);
    }
    // Clear any generated link when mode changes
    setInviteUrl('');
    setInviteSent(false);
    setError('');
  }, [inviteMode]);

  // Generate shareable invite link (Issue #646: only called on user action, not auto)
  // #1016: Pass max_uses based on invite mode
  const generateInviteLink = useCallback(async () => {
    if (generatingLink) return; // Prevent double-clicks

    setGeneratingLink(true);
    setError('');

    try {
      const response = await invitesApi.createClubMemberInvite(clubId, {
        type: inviteType,
        expires_in_days: expiresInDays,
        // #1016: null = unlimited uses for multiple mode, 1 = single use
        max_uses: inviteMode === 'multiple' ? null : 1,
      });
      setInviteUrl(response.invite.invite_url);
      toast.success(
        inviteMode === 'multiple'
          ? 'Reusable invite link created'
          : 'Single-use invite link created'
      );
    } catch (err) {
      const error = err as { message?: string; error?: string };
      const message = error.message || error.error || 'Failed to generate invite link';
      setError(message);
      toast.error(message);
    } finally {
      setGeneratingLink(false);
    }
  }, [clubId, inviteType, expiresInDays, inviteMode, generatingLink]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, onClose]);

  // Issue #646: Send invite creates a NEW invite (doesn't reuse shareable link).
  // #1016: Direct invites always use max_uses: 1.
  // #2623: recipient may be an email OR a phone — resolve/normalize before send.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Detect email vs phone, normalize phone to E.164 (Core Rule 27), and
    // surface the canonical validation message on a bad value. Never sends both
    // invitee_email and invitee_phone.
    const resolved = resolveInviteRecipient(recipient);
    if (resolved.kind === 'invalid') {
      setError(resolved.message);
      return;
    }

    setLoading(true);

    try {
      // Backend sends email (Resend) or SMS (TextGrid, #1454) based on which
      // field is present.
      const response = await invitesApi.createClubMemberInvite(clubId, {
        type: inviteType,
        expires_in_days: expiresInDays,
        ...(resolved.kind === 'email'
          ? { invitee_email: resolved.invitee_email }
          : { invitee_phone: resolved.invitee_phone }),
        max_uses: 1, // Direct invites are always single-use
      });

      setInviteUrl(response.invite.invite_url);
      setInviteSent(true);
      setRecipient('');
      const failure = inviteDeliveryFailure(response);
      setDeliveryFailure(failure);
      if (failure) {
        toast.error(inviteDeliveryFailureMessage(failure));
      } else {
        toast.success('Invite sent successfully');
      }
    } catch (err) {
      const error = err as Error;
      const message = error.message || 'Failed to send invite';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="club-invite-title"
    >
      {/* Backdrop - increased opacity for mobile readability (#900) */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-lg oro-fade-in"
        onClick={handleClose}
        aria-label="Close modal"
      />

      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-md oro-modal-enter">
        <Card className="border-oro-border/40 shadow-xl bg-oro-surface">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 id="club-invite-title" className="text-lg font-bold text-text-primary">
              Invite to {clubName}
            </h2>
            <button
              onClick={handleClose}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-surface-alt"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* #1016: Single vs Multiple Mode Toggle */}
          <div className="mb-4">
            <div className="flex rounded-lg bg-oro-surface-alt p-1 border border-oro-border">
              <button
                type="button"
                onClick={() => setInviteMode('multiple')}
                disabled={loading}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium oro-transition ${
                  inviteMode === 'multiple'
                    ? 'bg-accent text-black shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Users className="w-4 h-4" />
                Reusable
              </button>
              <button
                type="button"
                onClick={() => setInviteMode('single')}
                disabled={loading}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium oro-transition ${
                  inviteMode === 'single'
                    ? 'bg-accent text-black shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <User className="w-4 h-4" />
                Single Use
              </button>
            </div>
            <p className="text-xs text-text-muted mt-2 text-center">
              {inviteMode === 'multiple'
                ? 'Anyone with this link can join. Great for sharing publicly.'
                : 'One person per invite. Send directly to an email or phone.'}
            </p>
          </div>

          {/* Role Selection - Only show in single mode for leaders */}
          {inviteMode === 'single' && isLeader && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <UserPlus className="w-3.5 h-3.5 text-accent/90" />
                <label htmlFor="inviteType" className="block text-sm font-medium text-text-primary">
                  Invite as
                </label>
              </div>
              <select
                id="inviteType"
                className="border rounded-lg px-3 py-2 text-sm bg-surface-alt text-text-primary border-border focus:border-accent/60 w-full oro-transition focus:oro-focus"
                value={inviteType}
                onChange={(e) => setInviteType(e.target.value as 'club_member' | 'club_leader')}
                disabled={loading}
              >
                <option value="club_member">Member - Can RSVP and participate in events</option>
                <option value="club_leader">Leader - Can manage events and members</option>
              </select>
            </div>
          )}

          {/* Invite Link Section */}
          <div className={inviteMode === 'single' ? 'mb-4' : 'mb-6'}>
            <div className={`bg-oro-surface-alt/60 border border-oro-border/50 rounded-xl ${inviteMode === 'single' ? 'p-3' : 'p-5 rounded-2xl shadow-md'}`}>
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="w-4 h-4 text-accent/90" />
                <p className="text-sm font-medium text-text-primary">
                  {inviteMode === 'multiple' ? 'Shareable invite link' : 'Generate a link'}
                </p>
              </div>

              {generatingLink ? (
                <div className="flex items-center gap-2 text-text-muted">
                  <OroLoader variant="inline" decorative />
                  <span className="text-sm">Generating link...</span>
                </div>
              ) : inviteUrl ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-2 py-1.5 bg-surface-alt rounded text-xs text-accent break-all border">
                      {inviteUrl}
                    </code>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCopyLink}
                      className="shrink-0"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>

                  {/* QR Code Toggle (Issue #832) - only show in multiple mode to save space */}
                  {inviteMode === 'multiple' && (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-text-muted">
                          Share via Slack, Discord, text, or print a QR code.
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowQR(!showQR)}
                          className="shrink-0"
                        >
                          <QrCode className="w-4 h-4 mr-1" />
                          {showQR ? 'Hide QR' : 'Show QR'}
                        </Button>
                      </div>

                      {/* QR Code Display */}
                      {showQR && (
                        <div className="pt-2 border-t border-oro-border">
                          <QRCodeDisplay
                            url={inviteUrl}
                            size={180}
                            downloadFileName={`${clubName.replace(/\s+/g, '-').toLowerCase()}-invite-qr`}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className={inviteMode === 'single' ? 'flex items-center justify-between' : 'space-y-3'}>
                  <p className="text-sm text-text-secondary">
                    {inviteMode === 'multiple'
                      ? 'Create a link anyone can use to join as a member.'
                      : 'One-time link to share.'}
                  </p>
                  <Button
                    variant={inviteMode === 'multiple' ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={generateInviteLink}
                    disabled={generatingLink}
                  >
                    <LinkIcon className="w-4 h-4 mr-1" />
                    Generate
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* #1016: Email section only in single mode */}
          {inviteMode === 'single' && (
            <div className="border-t border-oro-border pt-4">
              <div className="flex items-center gap-2 mb-3">
                <MailPlus className="w-4 h-4 text-accent/90" />
                <h3 className="text-sm font-medium text-text-primary">
                  Or invite directly
                </h3>
              </div>

              {/* noValidate: the field accepts a phone too, so the browser's
                  native email validation must not block submit — we validate in
                  handleSubmit via resolveInviteRecipient (#2623). */}
              <form onSubmit={handleSubmit} className="space-y-3" noValidate>
                {/* Recipient and Expiry in a row on larger screens */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label htmlFor="recipient" className="block text-xs font-medium text-text-secondary mb-1">
                      Email or phone
                    </label>
                    <Input
                      id="recipient"
                      type="text"
                      // Neutral keyboard for the dual-purpose field — an email-
                      // biased inputMode gives phone-pasters the wrong soft
                      // keyboard on mobile web (matches native's static
                      // keyboardType="default"; George round 1).
                      inputMode="text"
                      autoComplete="off"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="email or phone"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="w-20">
                    <label htmlFor="expires" className="block text-xs font-medium text-text-secondary mb-1">
                      Days
                    </label>
                    <Input
                      id="expires"
                      type="number"
                      min="1"
                      max="365"
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Leader-facing SMS disclosure (#2628 round 2, George Medium):
                    the invite texts a third party, so the send consequence must
                    be visible before Send — not only in the backend's STOP
                    suffix. */}
                <p className="text-xs text-text-muted">
                  If you enter a phone number, the invite is sent by text. The
                  recipient can reply STOP to opt out.
                </p>

                {/* Error Message */}
                {error && (
                  <div className="p-2 bg-oro-error/10 border border-oro-error/30 rounded-lg">
                    <p className="text-sm text-oro-error">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {inviteSent && (
                  <div
                    className={
                      deliveryFailure
                        ? 'p-3 bg-oro-error/10 border border-oro-error/30 rounded-lg'
                        : 'p-3 bg-accent/10 border border-accent/30 rounded-lg'
                    }
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {deliveryFailure ? (
                        <AlertCircle className="w-4 h-4 text-oro-error" />
                      ) : (
                        <Check className="w-4 h-4 text-accent" />
                      )}
                      <span className="min-w-0 text-sm font-medium text-text-primary">
                        {deliveryFailure ? inviteDeliveryFailureMessage(deliveryFailure) : 'Invite Sent!'}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary mb-2">
                      Link: <code className="text-accent break-all">{inviteUrl}</code>
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setInviteSent(false);
                        setDeliveryFailure(null);
                        setRecipient('');
                      }}
                    >
                      Send Another
                    </Button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleClose}
                    disabled={loading}
                    className="flex-1 bg-oro-surface-alt/50 hover:bg-oro-surface-alt/70 border border-oro-border/50"
                  >
                    Done
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    disabled={!recipient.trim() || inviteSent}
                    className="flex-1"
                  >
                    Send Invite
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* #1016: Simple Done button for multiple mode */}
          {inviteMode === 'multiple' && (
            <div className="pt-4">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-oro-error/10 border border-oro-error/30 rounded-lg mb-4">
                  <p className="text-sm text-oro-error">{error}</p>
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
                className="w-full bg-oro-surface-alt/50 hover:bg-oro-surface-alt/70 border border-oro-border/50"
              >
                Done
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ClubMemberInviteModal;